import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../utils/supabase';
import { Release } from '../types';
import ExploreReleaseCard from '../components/ExploreReleaseCard';
import { useAppContext } from '../context/AppContext';
import { useReleases } from '../context/ReleasesContext';
import SearchBar from '../components/SearchBar';

// Add global CSS class for the slider thumbs
const sliderStyles = `
  .custom-range-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #7c3aed; /* Vibrant violet color */
    cursor: pointer;
    // border: 2px solid white;
    // box-shadow: 0 0 3px rgba(0, 0, 0, 0.2);
  }
  
  .custom-range-slider::-moz-range-thumb {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #7c3aed; /* Vibrant violet color */
    cursor: pointer;
    // border: 2px solid white;
    // box-shadow: 0 0 3px rgba(0, 0, 0, 0.2);
  }
`;

const RELEASES_PER_PAGE = 9;

// Time range options in days
const TIME_RANGE_OPTIONS = [
  { value: 0, label: 'Today' },
  { value: 6, label: '1 Week' },
  { value: 30, label: '1 Month' },
  { value: 90, label: '3 Months' },
  { value: 180, label: '6 Months' },
];

const Explore: React.FC = () => {
  const [releases, setReleases] = useState<Release[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadedReleaseIds, setLoadedReleaseIds] = useState<number[]>([]);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [timeRange, setTimeRange] = useState<number>(90); // Default to 3 months
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>(''); // Debounced by SearchBar component
  
  // Temporary states for filter panel
  const [tempTimeRange, setTempTimeRange] = useState<number>(90); // Default to 3 months
  const [tempGenres, setTempGenres] = useState<string[]>([]);
  
  const { userId } = useAppContext();
  const { toggleListen, getSessionToggle } = useReleases();

  // Helper to determine if a release is listened
  // For Explore page, server only returns unlistened releases, so default is false
  const isReleaseListened = useCallback((releaseId: number) => {
    return getSessionToggle(releaseId) ?? false;
  }, [getSessionToggle]);

  // Update temp filters when showing the panel
  useEffect(() => {
    if (showFilters) {
      setTempTimeRange(timeRange);
      setTempGenres([...selectedGenres]);
    }
  }, [showFilters, timeRange, selectedGenres]);

  const fetchUnlistenedReleases = useCallback(async (previousIds: number[] = []): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      // Pass timeRange and searchTerm to the backend
      const { data: releases, error: releasesError } = await supabase
        .rpc('get_random_releases', {
          user_id: userId,
          label_ids: [],
          exclude_ids: previousIds,
          num_releases: RELEASES_PER_PAGE,
          genre_filters: selectedGenres.length > 0 ? selectedGenres : null,
          time_range_days: timeRange, // Add time range parameter
          search_term: searchTerm || null // Add search term parameter (debounced by SearchBar)
        });

      if (releasesError) {
        console.error('RPC error:', releasesError);
        throw releasesError;
      }

      if (!releases || releases.length === 0) {
        setReleases([]);
        setLoadedReleaseIds([]);
        return;
      }

      const validReleases = releases.filter((release: Release) => 
        Array.isArray(release.genres) && release.genres.length > 0
      );

      if (validReleases.length === 0) {
        setReleases([]);
        setLoadedReleaseIds([]);
        return;
      }

      const newIds = validReleases.map((release: Release) => release.id!);
      setReleases(validReleases);
      setLoadedReleaseIds([...previousIds, ...newIds]);

    } catch (error) {
      console.error('Error fetching releases:', error);
      if (error instanceof Error) {
        setError('Failed to load releases: ' + error.message);
      } else {
        setError('Failed to load releases: ' + String(error));
      }
    } finally {
      setLoading(false);
    }
  }, [userId, selectedGenres, timeRange, searchTerm]); // Add timeRange and searchTerm to dependency array

  const handleToggleCheck = useCallback(async (releaseId: number) => {
    const currentlyListened = isReleaseListened(releaseId);
    await toggleListen(releaseId, currentlyListened);
  }, [toggleListen, isReleaseListened]);

  const handleShuffle = useCallback(() => {
    fetchUnlistenedReleases(loadedReleaseIds);
  }, [loadedReleaseIds, fetchUnlistenedReleases]);

  const handleTagClick = useCallback((tag: string) => {
    if (showFilters) {
      // Update temp genres when panel is open
      setTempGenres(prevGenres => {
        // If the tag is already selected, remove it
        if (prevGenres.includes(tag)) {
          return prevGenres.filter(genre => genre !== tag);
        }
        // Otherwise, add it to the array
        return [...prevGenres, tag];
      });
    } else {
      // Immediate update when panel is closed (existing behavior)
      setSelectedGenres(prevGenres => {
        // If the tag is already selected, remove it
        if (prevGenres.includes(tag)) {
          return prevGenres.filter(genre => genre !== tag);
        }
        // Otherwise, add it to the array
        return [...prevGenres, tag];
      });
      
      // Reset IDs to start fresh
      setLoadedReleaseIds([]);
    }
  }, [showFilters]);

  const applyFilters = useCallback(() => {
    setTimeRange(tempTimeRange);
    setSelectedGenres(tempGenres);
    setLoadedReleaseIds([]);
    setShowFilters(false);
  }, [tempTimeRange, tempGenres]);

  const clearAllFilters = useCallback(() => {
    if (showFilters) {
      // Just clear temp filters when panel is open - reset to default of 3 months
      setTempTimeRange(90);
      setTempGenres([]);
    } else {
      // Clear actual filters and refresh - reset to default of 3 months
      setSelectedGenres([]);
      setTimeRange(90);
      setSearchTerm('');
      setLoadedReleaseIds([]);
    }
  }, [showFilters]);

  // Format time range for display
  const getTempTimeRangeDescription = useCallback(() => {
    const option = TIME_RANGE_OPTIONS.find(opt => opt.value === tempTimeRange);
    return option ? option.label : '';
  }, [tempTimeRange]);

  const handleTimeRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    const option = TIME_RANGE_OPTIONS[value];
    setTempTimeRange(option.value);
  };

  // Fetch releases on initial load and when filters change
  useEffect(() => {
    if (userId) {
      fetchUnlistenedReleases([]);
    }
  }, [userId, fetchUnlistenedReleases, selectedGenres, timeRange, searchTerm]);

  return (
    <div className="min-h-screen relative">
      {/* Side Filter Panel */}
      <div 
        className={`fixed top-0 right-0 h-full w-64 bg-orange-400/95 shadow-lg z-20 transform transition-transform duration-300 ease-in-out ${
          showFilters ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="p-4 h-full flex flex-col">
          <div className="flex justify-between items-center mb-6 pb-3 border-b">
            <h3 className="font-medium text-gray-800">Filters</h3>
            <button 
              onClick={() => setShowFilters(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
          
          {/* Time Range Filter */}
          <div className="mb-6">
            <label htmlFor="time-range" className="block text-base font-medium text-gray-700 mb-3">
              Time Range: <span className="text-violet-700 text-lg font-semibold">{getTempTimeRangeDescription()}</span>
            </label>
            
            {/* Simplified slider container */}
            <div className="relative pt-1 pb-8">
              {/* Slider input with custom styling for the knob */}
              <input
                id="time-range"
                type="range"
                min="0"
                max="4"
                step="1"
                value={TIME_RANGE_OPTIONS.findIndex(opt => opt.value === tempTimeRange)}
                onChange={handleTimeRangeChange}
                className="w-full h-3 bg-violet-100 rounded-lg appearance-none cursor-pointer mb-4 custom-range-slider"
                style={{
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  outline: 'none',
                }}
              />
              
              {/* Add custom CSS in the head */}
              <style dangerouslySetInnerHTML={{ __html: sliderStyles }} />
              
              {/* Text indicators below slider */}
              <div className="flex justify-between px-[1px] mt-1">
                {TIME_RANGE_OPTIONS.map((option, index) => (
                  <span 
                    key={option.value}
                    className={`cursor-pointer transition-all ${
                      tempTimeRange === option.value 
                        ? 'text-violet-600 font-medium text-base' 
                        : 'text-gray-600 text-sm'
                    }`}
                    onClick={() => setTempTimeRange(option.value)}
                    style={{                      
                      width: '24px',
                      textAlign: 'center'
                    }}
                  >
                    {index === 0 ? 'T' : index === 1 ? '1W' : index === 2 ? '1M' : index === 3 ? '3M' : '6M'}
                  </span>
                ))}
              </div>
            </div>
          </div>
          
          {/* Reset and Apply buttons */}
          <div className="mt-auto pt-4 border-t flex justify-between">
            <button
              onClick={applyFilters}
              className="w-full py-2 px-4 bg-violet-500 text-white font-medium rounded-md hover:bg-violet-600 transition-colors"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
      
      {/* Overlay when filter panel is open */}
      {showFilters && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-25 z-10"
          onClick={() => setShowFilters(false)}
        ></div>
      )}

      <main className="pt-16 pb-6 px-6 max-w-[1080px] mx-auto">
        <div className="flex justify-between items-start mb-8 border-b border-violet-300 pb-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-800 mb-2">Explore Releases</h1>
            <p className="text-gray-700">Discover random new releases from labels that your community follows</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Filter button */}
            <button
              onClick={() => setShowFilters(true)}
              className="p-2 rounded-lg bg-violet-100 text-violet-600 hover:bg-violet-200 transition-colors"
              aria-label="Show filters"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
              </svg>
            </button>
            <button
              onClick={handleShuffle}
              disabled={loading}
              className={`px-4 py-2 rounded-lg bg-violet-500 text-white font-medium hover:bg-violet-600 transition-colors ${
                loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading ? 'Loading...' : 'Shuffle'}
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search by release title, artist, genre, or label..."
          />
        </div>

        {error && (
          <div className="text-red-600 mb-4">{error}</div>
        )}

        {/* Active Filters Display */}
        <div className="mb-6 flex flex-wrap items-center gap-2">
          {selectedGenres.length > 0 && (
            <>
              <span className="text-sm font-medium text-gray-700 mr-1">
                {selectedGenres.length > 1 ? 'Genre filters:' : 'Genre filter:'}
              </span>
              
              {selectedGenres.map(genre => (
                <span key={genre} className="px-3 py-1.5 bg-violet-100 text-violet-800 rounded-lg flex items-center gap-1 text-sm font-medium shadow-sm border border-violet-200">
                  {genre}
                  <button
                    onClick={() => handleTagClick(genre)}
                    className="ml-1.5 text-violet-500 hover:text-violet-800"
                    aria-label={`Remove ${genre} filter`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </button>
                </span>
              ))}
              
              <button
                onClick={clearAllFilters}
                className="px-3 py-1.5 bg-orange-400 text-gray-700 rounded-lg hover:bg-orange-200 transition-colors duration-100 text-sm font-medium flex items-center gap-1 shadow-sm"
              >
                Clear all
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </>
          )}
        </div>

        {releases.length === 0 && !loading ? (
          <p className="text-gray-600">
            No releases found. Try adjusting your filters or shuffling.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {releases.map((release) => (
              <div key={release.id} className="w-full">
                <ExploreReleaseCard 
                  release={release} 
                  labelId={release.label_id!} 
                  labelName={release.labels?.label_name || ''}
                  isChecked={isReleaseListened(release.id!)}
                  onToggleCheck={handleToggleCheck}
                  onTagClick={handleTagClick}
                  selectedGenres={selectedGenres}
                />
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Explore;